import { api } from '@/global/services/api'
import { isObject } from '@/global/utils/utils'
import { dateService, FORMATS } from '@/global/services/date'
import documentsHistoryHelper from '../documents-history-helper'
import { notify } from '@/global/services/notification/notificationBus'
import tabsHelper from '../tabs-helper'

const DOCUMENTS_HISTORY_DATA_KEY = 'conclusionDocuments'
const DOCUMENT_TYPE = 'amendment_conclusion'

export default {
  name: 'ConclusionOnAmendment',

  mixins: [
    documentsHistoryHelper,
    tabsHelper
  ],

  data () {
    return {
      conclusionHistoryVisible: false,
      conclusionShowEditButtons: false,
      conclusionEditState: false,
      conclusionShowDocumentPreview: false,
      conclusionDocumentContent: null,
      conclusionDataForGenerationVisible: true,
      conclusionTitle: this.$t('road-maintenance/aggregator.conclusion_on_amendment'),
      conclusionBaseTitle: this.$t('road-maintenance/aggregator.conclusion_on_amendment'),
      conclusionValidationErrors: {},
      conclusionDocumentHistoryLoaded: false,
      conclusionRefreshDisabled: false,
      coaDownloadDocumentLoaders: [],
      showCoaAmendmentTabLoader: null
    }
  },

  computed: {
    conclusion () {
      return {
        title: this.$t('road-maintenance/aggregator.conclusion_on_amendment'),
        key: 'conclusion',
        visible: this.$route.query.category !== 'request_new',
        validationErrors: this.conclusionValidationErrors,
        apiConfig: {
          post: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}/store`,
            usePatch: false,
            method: 'post'
          },
          get: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`,
            method: 'get'
          }
        },
        edit: this.conclusionEditState,
        editUpdate: (event) => this.onConclusionEditUpdate(event),
        dataFetched: (event) => this.onConclusionDataFetched(event),
        afterCancel: (event) => this.onConclusionDataFetched(event),
        afterSave: (event) => this.onConclusionDataFetched(event, true),
        modifyDataBeforeSend: (event) => this.onConclusionModifyData(event),
        showLoader: this.showCoaAmendmentTabLoader,
        hiddenFormKeys: [
          'html'
        ],
        tabContent: [
          {
            component: this.tabActionsComponent,
            title: this.conclusionTitle,
            showBackButton: {
              onGoBack: () => this.goBack()
            },
            showEditButtons: this.conclusionShowEditButtons,
            forceAdditionalButtonsDisplay: true,
            additional_buttons: [
              {
                type: 'base',
                appendIcon: 'mdi-refresh',
                visible: !this.conclusionShowEditButtons,
                disabled: this.conclusionRefreshDisabled,
                click: async (event) => {
                  const { fetchTabData } = event || {}
                  this.conclusionRefreshDisabled = true

                  await fetchTabData?.()
                  await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

                  setTimeout(() => {
                    this.conclusionRefreshDisabled = false
                  }, this.refreshTabBtnTimeout)
                }
              },
              {
                type: 'base',
                label: this.$t('fleet/documents/prl_cargo.change_status'),
                prependIcon: 'mdi-swap-horizontal',
                visible: !this.conclusionShowEditButtons && !!this.permissions?.canChangeStatus,
                click: (event) => this.onChangeStatus(event)
              },
              {
                type: 'dropdown',
                buttonType: 'base',
                label: this.$t('road-maintenance/aggregator.generate_document'),
                color: '#65AD45',
                class: 'generate-document-btn',
                visible: !this.conclusionShowEditButtons && !!this.permissions?.canCreate,
                hasHistoryData: this.logsData?.[DOCUMENTS_HISTORY_DATA_KEY]?.length,
                menuContentClass: 'menu-class',
                component: this.documentsGenerationDropdownMenuComponent,
                menuItemClick: (event) => this.conclusionMenuItemClick(event)
              },
              {
                type: 'base',
                label: this.viewPdfBtnLabel,
                color: '#6e736d',
                visible: this.conclusionShowEditButtons,
                class: 'generate-document-btn',
                loaderConfig: {
                  opacity: 0,
                  color: '#FFFFFFF'
                },
                showLoader: this.showPreviewLoader,
                click: (event) => this.previewDocument(event, DOCUMENT_TYPE, this.conclusionBaseTitle)
              }
            ],
            onCancelTabSave: () => this.onConclusionCancelTab(),
            onSaveClick: (event) => this.onConclusionSave(event)
          },
          // Data for document generation
          {
            component: this.columnsContentComponent,
            title: this.$t('road-maintenance/aggregator.data_for_document_generation'),
            visible: this.conclusionDataForGenerationVisible,
            rows: [
              {
                type: 'fields',
                columnsCount: 3,
                fields: [
                  {
                    key: 'doc_class',
                    label: this.$t('road-maintenance/aggregator.class'),
                    row: 1,
                    column: 1,
                    colSpan: 1,
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'registry_number',
                    row: 1,
                    column: 2,
                    colSpan: 1,
                    label: this.$t('road-maintenance/excavation-service/tabs/approval/columns.registration_number'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'created_at',
                    type: 'datetime-picker',
                    subType: 'date',
                    row: 1,
                    column: 3,
                    colSpan: 1,
                    appendIcon: 'mdi-calendar',
                    format: FORMATS.date,
                    placeholder: FORMATS.date,
                    label: this.$t('road-maintenance/aggregator.document_date'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          created_at: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'applicant',
                    row: 2,
                    column: 1,
                    colSpan: 1,
                    label: this.$t('road-maintenance/prp.request_applicant'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'addresses',
                    row: 2,
                    column: 2,
                    colSpan: 2,
                    label: this.$t('road-maintenance/aggregator.request_locations'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'work_type',
                    type: 'textarea',
                    row: 3,
                    column: 1,
                    colSpan: 1,
                    label: this.$t('road-maintenance/aggregator.type_of_works'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'amendments',
                    type: 'textarea',
                    row: 4,
                    column: 1,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.request_required_additions'),
                    unlock: !!this.permissions?.canCreate,
                    creatable: true,
                    editable: true,
                    visible: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'amendments_note',
                    type: 'textarea',
                    row: 4,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.request_required_additions_explanation'),
                    unlock: !!this.permissions?.canCreate,
                    creatable: true,
                    editable: true,
                    visible: true,
                    doubleClickEdit: false
                  }
                ]
              }
            ]
          },
          // Document history
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-blocks-main',
            wrapperClasses: 'aggregator-requests-subject-wrapper',
            title: this.$t('road-maintenance/aggregator.log_history'),
            visible: this.conclusionHistoryVisible,
            externalData: this.logsData,
            dataKey: DOCUMENTS_HISTORY_DATA_KEY,
            columnsConfig: [
              {
                key: 'created_at',
                title: this.$t('road-maintenance.date') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.created_at ? dateService.formatCustom(data.created_at, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              {
                key: 'status',
                title: this.$t('road-maintenance/excavation-service.status') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.status ? this.$t(data.status) : '-'
                }
              },
              {
                key: 'user',
                title: this.$t('road-maintenance.user') + ':',
                valueClass: 'history-block-value'
              },
              {
                key: 'document',
                title: this.$t('road-maintenance/aggregator.document') + ':',
                valueClass: 'history-block-value',
                visible: (event) => {
                  return event?.blockValue !== '-'
                },
                loaderIds: this.coaDownloadDocumentLoaders,
                prependImage: {
                  src: '/img/icons/pdf-icon.svg',
                  style: {
                    marginRight: '4%'
                  }
                },
                contentWrapperStyle: {
                  display: 'flex',
                  alignItems: 'center'
                },
                customValue: (data) => {
                  return data?.document?.name ? data.document.name : '-'
                },
                click: (event) => this.onCoaDownloadDocument(event)
              },
              {
                visible: (event) => {
                  return !!event?.rowData?.document_data_id
                },
                component: this.createFromVersionBtnComponent,
                class: 'create-from-version-btn-block',
                onClick: (event) => this.onCoaCreateFromVersion(event)
              }
            ]
          },
          {
            component: this.documentPreviewComponent,
            visible: this.conclusionShowDocumentPreview,
            content: this.conclusionDocumentContent
          }
        ]
      }
    }
  },

  methods: {
    async onCoaDownloadDocument (event) {
      const { id: documentId } = event?.rowData || {}
      // skip if download is already in progress
      if (this.coaDownloadDocumentLoaders.includes(documentId)) {
        return
      }
      // show document loader
      if (documentId && !this.coaDownloadDocumentLoaders.includes(documentId)) {
        this.coaDownloadDocumentLoaders.push(documentId)
      }

      // download document
      await this.downloadDocument(event)
      // stop document loader
      if (this.coaDownloadDocumentLoaders.includes(documentId)) {
        this.coaDownloadDocumentLoaders = this.coaDownloadDocumentLoaders.filter(activeLoaderId => activeLoaderId !== documentId)
      }
    },

    async onConclusionGenerateDocument (event, showLast = false) {
      try {
        const { formData } = event || {}
        const { id } = this?.$route?.params || {}

        this.showCoaAmendmentTabLoader = true
        if (!id) {
          console.error('Missing traffic regulation id')
          notify(this.$t('road-maintenance/aggregator.generate_document_not_possible'))
          return
        }
        if (!isObject(formData)) {
          notify(this.$t('road-maintenance/aggregator.generate_document_not_possible'))
          console.error('Form data is invalid', formData)
          return
        }

        // Create formData object for sending
        const dataToSend = new FormData()
        dataToSend.append('amendments', formData?.amendments ?? '')
        dataToSend.append('amendments_note', formData?.amendments_note ?? '')
        dataToSend.append('show_last_version', showLast)
        dataToSend.append('created_at', formData?.created_at ? dateService.formatCustom(formData.created_at, FORMATS.date, FORMATS.isoDate) : '')

        // Get document html
        const { data } = await api('road-maintenance').post(`traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`, dataToSend)

        this.conclusionDocumentContent = data
        this.conclusionShowDocumentPreview = true
        this.conclusionEditState = true
        this.conclusionShowEditButtons = true
        this.conclusionDataForGenerationVisible = false
        this.conclusionTitle = this.$t('road-maintenance/aggregator.conclusion_document_overview')
      }
      catch (exception) {
        const { errors } = exception?.response?.data || {}
        this.conclusionValidationErrors = errors && isObject(errors) ? errors : {}
        console.error(exception)
      }
      finally {
        this.showCoaAmendmentTabLoader = false
      }
    },

    async onCoaCreateFromVersion (event) {
      try {
        this.showCoaAmendmentTabLoader = true

        // get document data
        const data = await this.createFromVersion(event, DOCUMENT_TYPE)

        const { html, css } = data || {}
        // do not open document if data is invalid
        if (!html && !css) {
          return
        }

        // set document data and open document preview
        this.conclusionDocumentContent = data
        this.conclusionShowDocumentPreview = true
        this.conclusionEditState = true
        this.conclusionShowEditButtons = true
        this.conclusionDataForGenerationVisible = false
        this.conclusionTitle = this.$t('road-maintenance/aggregator.conclusion_document_overview')
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        this.showCoaAmendmentTabLoader = false
      }
    },

    onConclusionSave (event) {
      const { saveTab } = event || {}
      this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
      this.$set(this.informationDialogConfig, 'cancelButtonText', this.$t('base.cancel'))
      this.$set(this.informationDialogConfig, 'message', this.$t('road-maintenance/aggregator.document_will_be_uploaded_to_centrix_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.onSaveConfirmation(saveTab))
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    onSaveConfirmation (saveTabFn) {
      if (typeof saveTabFn === 'function') {
        saveTabFn()
      }

      this.conclusionEditState = false
      this.$set(this.informationDialogConfig, 'showDialog', false)
      this.$set(this.informationDialogConfig, 'title', this.informationDialogBaseConfig.title)
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', this.informationDialogBaseConfig.confirmCallbackFunction)
    },

    onConclusionCancelTab () {
      this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
      this.$set(this.informationDialogConfig, 'cancelButtonText', this.$t('base.cancel'))
      this.$set(this.informationDialogConfig, 'message', this.$t('fleet/vehicle.data_not_saved_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.conclusionCancel())
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    conclusionCancel () {
      this.conclusionEditState = false
      this.$set(this.informationDialogConfig, 'showDialog', false)
    },

    onConclusionEditUpdate (event) {
      const { edit } = event || {}
      this.conclusionHistoryVisible = !edit

      if (!edit) {
        this.conclusionShowDocumentPreview = false
        this.conclusionShowEditButtons = false
        this.conclusionEditState = false
        this.conclusionDocumentContent = null
        this.conclusionDataForGenerationVisible = true
        this.conclusionTitle = this.conclusionBaseTitle
      }
    },

    async onConclusionDataFetched (event, afterSave = false) {
      const { data: apiData, updateData } = event || {}
      const { formDataForSend } = event || {}

      const data = apiData?.data || formDataForSend || {}

      if (typeof updateData === 'function' && data && isObject(data)) {
        updateData({
          created_at: data?.created_at && !dateService.isValid(data.created_at, FORMATS.date, true) ? dateService.formatCustom(data.created_at, FORMATS.isoDate, FORMATS.date) : dateService.now(FORMATS.date)
        })
      }

      if (!this.conclusionDocumentHistoryLoaded || afterSave) {
        this.conclusionDocumentHistoryLoaded = true
        const { id: suggestedStatusId, description } = data?.suggested_status || {}

        // Fetch documents logs
        await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

        // Open change status popup after documents save only if we have suggested status provided
        if (suggestedStatusId) {
          this.$set(this.afterDocumentStatusChangeActions, 'statusId', suggestedStatusId)
          const message = this.$t('road-maintenance/aggregator.status_change_message')
          this.$set(this.informationDialogConfig, 'showDialog', afterSave)
          this.$set(this.informationDialogConfig, 'message', '<div> ' + message + '<span style="font-style: italic; color: green;">' + ' ' + this.$t(description) + '</span>?</div>')
        }
      }
    },

    onConclusionModifyData (event) {
      const { originalData } = event || {}

      if (originalData) {
        originalData.created_at = originalData?.created_at ? dateService.formatCustom(originalData.created_at, FORMATS.date, FORMATS.isoDate) : null
      }

      return originalData
    },

    async conclusionMenuItemClick (event) {
      const { menuIndex } = event || {}
      await this.onConclusionGenerateDocument(event, menuIndex === 1)
    }
  }
}
